// PC-193: SHA-256 file hashing for FIPS-compliant duplicate detection.
// Replaces spark-md5 (MD5 is not FIPS 140-2 approved).
// Uses the Web Crypto API (SubtleCrypto) which is available in all modern
// browsers and does not require any npm dependency.

/**
 * Calculate SHA-256 hash of a File object.
 * Returns a lowercase hex string (64 chars), e.g. "a3f1...".
 *
 * @param {File} file
 * @returns {Promise<string>} hex-encoded SHA-256 digest
 */
export async function calculateSHA256(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const buffer = event.target.result;
        // Normalize to Uint8Array — JSDOM's FileReader may return a Node.js
        // Buffer (Uint8Array subclass) rather than a plain ArrayBuffer.
        // Node.js webcrypto's subtle.digest() rejects Node Buffers in some
        // versions, but reliably accepts any TypedArray.
        const bytes = new Uint8Array(buffer);
        const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hexHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hexHash);
      } catch (error) {
        reject(new Error(`SHA-256 calculation failed: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file for SHA-256 calculation'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * @deprecated Use calculateSHA256 instead.
 * Kept as an alias so any callers not yet updated continue to work.
 * Will be removed after all consumers are migrated.
 */
export const calculateMD5 = calculateSHA256;
